/**
 * AdminExecutiveReports — admin route to generate & manage executive PDFs
 * across all clients.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import ExecutiveReportDialog from "@/components/ExecutiveReportDialog";
import ExecutiveReportsList from "@/components/ExecutiveReportsList";

export default function AdminExecutiveReports() {
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");
    const [reports, setReports] = useState([]);
    const [floorPlans, setFloorPlans] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filterClient, setFilterClient] = useState("all"); // 'all' or client_id

    // Load clients once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/clients");
                setClients(data || []);
            } catch (e) {
                console.error("clients load", e);
            }
        })();
    }, []);

    // Load floor plans whenever the *selected* client (for new report) changes
    useEffect(() => {
        if (!selectedClient) {
            setFloorPlans([]);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/floor-plans", { params: { client_id: selectedClient } });
                setFloorPlans(data || []);
            } catch (e) {
                console.error("floor-plans load", e);
                setFloorPlans([]);
            }
        })();
    }, [selectedClient]);

    // Load reports (filtered by client or all)
    const reloadReports = async () => {
        try {
            const params = {};
            if (filterClient && filterClient !== "all") params.client_id = filterClient;
            const { data } = await api.get("/executive-reports", { params });
            setReports(data || []);
        } catch (e) {
            console.error("reports load", e);
        }
    };
    useEffect(() => { reloadReports(); }, [filterClient]);

    const clientLookup = useMemo(() => {
        const m = {};
        clients.forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [clients]);

    const openGenerator = () => {
        if (!selectedClient) {
            toast.error("Selecciona el cliente para el cual generar el informe");
            return;
        }
        setDialogOpen(true);
    };

    const onDelete = async (r) => {
        if (!window.confirm(`¿Eliminar el informe "${r.period_label}"?`)) return;
        try {
            await api.delete(`/executive-reports/${r.id}`);
            toast.success("Informe eliminado");
            reloadReports();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo eliminar");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-executive-reports">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Informes Ejecutivos
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Genera y gestiona informes de actividad MIP para tus clientes.
                    </p>
                </div>
            </div>

            {/* Generator */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Generar nuevo informe
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                        <div>
                            <Label className="text-xs">Cliente</Label>
                            <Select value={selectedClient} onValueChange={setSelectedClient}>
                                <SelectTrigger className="mt-1" data-testid="admin-exec-client-select">
                                    <SelectValue placeholder="Selecciona un cliente…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={openGenerator} disabled={!selectedClient}
                                className="bg-amber-500 text-black hover:bg-amber-400"
                                data-testid="admin-open-exec-dialog">
                            <Sparkles className="w-4 h-4 mr-1.5" /> Generar informe
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* List */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
                        <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-600" />
                            Histórico de informes ({reports.length})
                        </span>
                        <Select value={filterClient} onValueChange={setFilterClient}>
                            <SelectTrigger className="w-[220px]" data-testid="admin-exec-filter-client">
                                <SelectValue placeholder="Filtrar por cliente…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los clientes</SelectItem>
                                {clients.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <ExecutiveReportsList
                        reports={reports}
                        onDelete={onDelete}
                        showClient={true}
                        clientLookup={clientLookup}
                    />
                </CardContent>
            </Card>

            <ExecutiveReportDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                floorPlans={floorPlans}
                clientId={selectedClient}
                onGenerated={() => reloadReports()}
            />
        </div>
    );
}
